import { useCallback, useEffect, useRef, useState } from "react";
import { Layer, Line, Rect, Stage } from "react-konva";
import { Html as _Html } from "react-konva-utils";
import styled from "styled-components";
import _ReactRough, { Rectangle } from "../lib/react-rough";
import { NumberWithBottomArrow } from "./uiElements/NumberWithBottomArrow";
import { NumberWithLeftArrow } from "./uiElements/NumberWithLeftArrow";
import { NumberWithRightArrow } from "./uiElements/NumberWithRightArrow";
import { NumberWithTopArrow } from "./uiElements/NumberWithTopArrow";
import { useLocation } from "react-router-dom";
import { createBoard, toPos } from "../puzzle/board";
import { decodeArrowNumber16 } from "../lib/decode";
import { pathfinder } from "../puzzle/pathfinder";
import { useYajilinAnswer } from "../puzzle/yajilin/useYajilinAnswer";
import { useYajilinSolver } from "../puzzle/yajilin/useYajilinSolver";
import { data, ProblemData } from "../data/data";
import { equals } from "../puzzle/Position";
import { LineState } from "../puzzle/yajilin/answerModel";

const fullHeight = window.innerHeight;
const fullWidth = window.innerWidth;

type Props = {};

export const Yajilin = () => {
  const location = useLocation();
  const arr = location.search.substring(1).split("/");
  const [puzzleType, colString, rowString, _boardCode] =
    arr.length === 5 ? arr.filter((e, i) => i !== 1) : arr;
  const _col = parseInt(colString);
  const _row = parseInt(rowString);
  const cellSize = 100;
  const board = createBoard(_col, _row);
  const [boardCode, setBoardCode] = useState(_boardCode ?? "");
  let code = decodeArrowNumber16(boardCode ?? "", board);
  const boardWidth = board.cols * cellSize;
  const boardHeight = board.rows * cellSize;
  const center = 1000;
  const overRef1 = useRef<any>(null);
  const overRef2 = useRef<any>(null);
  const [cx, setCx] = useState(-1);
  const [cy, setCy] = useState(-1);
  const [redraw, setRedraw] = useState(0);
  const [lines, setLines] = useState<{ tool: string; points: number[] }[]>([]);
  const isDrawing = useRef(false);
  const [currentProblem, setCurrentProblem] = useState(0);
  const [solvedCount, setSolvedCount] = useState(0);
  const [unSolvedCount, setUnSolvedCount] = useState(0);
  const [problem, setProblem] = useState<ProblemData | null>(null);

  const answers = useYajilinAnswer();
  const {
    addBlackCell,
    addWhiteCell,
    removeBlackCell,
    removeWhiteCell,
    fixedLines,
    hasLine,
    addLine,
    removeLine,
  } = answers;

  const solver = useYajilinSolver(board, answers);

  const [time, updateTime] = useState(Date.now());

  useEffect(() => {
    if (puzzleType !== "yajilinAuto") {
      return;
    }
    if (solvedCount === -1) {
      return;
    }
    if (!problem) {
      return;
    }
    // console.log("start", problem.index);
    const status = solver.runAll(problem.index);
    let timeoutId: NodeJS.Timeout | null = null;
    if (status !== "broken") {
      timeoutId = setTimeout(() => updateTime(Date.now()), 10);
      // console.log("setTimeout");
    } else {
      console.log("broken", problem.index);
    }
    setRedraw((prev) =>
      prev < 1498 && answers.isSingleLine() ? 1498 : prev + 1
    );
    if (status === "completed" /*answers.isSingleLine()*/) {
      // console.log("solved");
      setSolvedCount((prev) => prev + 1);
      setCurrentProblem((prev) => prev + 1);
    } else if (status === "stopped") {
      console.log("unsolved", problem?.index);
      setUnSolvedCount((prev) => prev + 1);
      setCurrentProblem((prev) => prev + 1);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [time]);

  useEffect(() => {
    if (puzzleType !== "yajilinAuto") {
      return;
    }
    board.cols = 10;
    board.rows = 10;
    // const problem = data.filter((_) => _.index === "950")[currentProblem];
    const problem = data[currentProblem];
    setProblem(problem ?? null);
    if (solvedCount === -1) {
      return;
    }
    if (!problem) {
      console.log("finished");
      console.log("solvedCount", solvedCount);
      console.log("unSolvedCount", unSolvedCount);
      setSolvedCount(-1);
      return;
    }

    const arr = problem.code.split("/");
    const [_, colString, rowString, _boardCode] =
      arr.length === 5 ? arr.filter((e, i) => i !== 1) : arr;
    setBoardCode(_boardCode);
    answers.clear();
    setRedraw(1000);
    updateTime(Date.now());
  }, [currentProblem]);

  const onRightClick = useCallback(
    (x: number, y: number) => {
      if (board.cell[y * board.cols + x].qnum !== -1) {
        const status = solver.run();
        // console.log(status);
        return;
      }
      if (answers.hasWhiteCell({ x, y })) {
        removeWhiteCell({ x, y });
      } else {
        addWhiteCell({ x, y });
      }
    },
    [answers]
  );
  const onClick = useCallback(
    (x: number, y: number) => {
      if (board.cell[y * board.cols + x].qnum !== -1) {
        return;
      }

      setCx(x);
      setCy(y);
      setRedraw((prev) => prev + 1);
      if (answers.hasWhiteCell({ x, y })) {
        removeBlackCell({ x, y });
        return;
      }

      const target1 = overRef1.current;
      target1.to({
        opacity: 1,
        points: [
          0.01 * cellSize,
          0.01 * cellSize,
          0.01 * cellSize,
          0.01 * cellSize,
          0.99 * cellSize,
          0.01 * cellSize,
          0.01 * cellSize,
          0.99 * cellSize,
        ],
        duration: 0,
      });
      window.setTimeout(() => {
        addBlackCell({ x, y });
        target1.to({
          points: [
            0.99 * cellSize,
            0.01 * cellSize,
            0.01 * cellSize,
            0.99 * cellSize,
            0.99 * cellSize,
            0.01 * cellSize,
            0.01 * cellSize,
            0.99 * cellSize,
          ],
          duration: 0.15,
        });
      }, 0);
      window.setTimeout(() => {
        target1.to({
          opacity: 0,
          duration: 0,
        });
        const target2 = overRef2.current;
        //
        target2.to({
          points: [
            0.99 * cellSize,
            0.99 * cellSize,
            0.99 * cellSize,
            0.99 * cellSize,
            0.99 * cellSize,
            0.99 * cellSize,
          ],
          duration: 0.15,
        });
      }, 150);
    },
    [answers]
  );

  const handleMouseDown = (e: any) => {
    setCx(-1);
    setCy(-1);
    isDrawing.current = true;
    const displayPos = e.target.getStage().getPointerPosition();
    const precisePoint = [
      (displayPos.x - (center - boardWidth / 2)) / cellSize,
      (displayPos.y - (center - boardHeight / 2)) / cellSize,
    ];
    const pos = {
      x: Math.floor(precisePoint[0]),
      y: Math.floor(precisePoint[1]),
    };
    setLines([
      ...lines,
      {
        tool: hasLine(pos) ? "eraser" : "pen",
        points: [displayPos.x, displayPos.y],
      },
    ]);
  };

  const handleMouseMove = (e: any) => {
    setCx(-1);
    setCy(-1);
    if (!isDrawing.current) {
      return;
    }
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    let lastLine = lines[lines.length - 1];
    // add point
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    const preciseFirstPoint = {
      x: (lastLine.points[0] - (center - boardWidth / 2)) / cellSize,
      y: (lastLine.points[1] - (center - boardHeight / 2)) / cellSize,
    };
    const firstPoint = {
      x: Math.floor(preciseFirstPoint.x),
      y: Math.floor(preciseFirstPoint.y),
    };
    const preciseLastPoint = {
      x: (point.x - (center - boardWidth / 2)) / cellSize,
      y: (point.y - (center - boardHeight / 2)) / cellSize,
    };
    const lastPoint = {
      x: Math.floor(preciseLastPoint.x),
      y: Math.floor(preciseLastPoint.y),
    };

    const isEraser = fixedLines.some(
      (line) =>
        line.some((point) => equals(point, firstPoint)) &&
        line.some((point) => equals(point, lastPoint))
    );

    if (isEraser) {
      lastLine.tool = "eraser";
    }

    // replace last
    lines.splice(lines.length - 1, 1, lastLine);
    setLines(lines.concat());
  };

  const handleMouseUp = () => {
    setCx(-1);
    setCy(-1);
    const lastLine = lines[lines.length - 1];
    const displayPoints = lastLine.points
      .map((e, i) => {
        return i % 2 === 0 ? [e, lastLine.points[i + 1]] : null;
      })
      .filter((e) => e !== null) as number[][];

    {
      const preciseFirstPoint = [
        (lastLine.points[0] - (center - boardWidth / 2)) / cellSize,
        (lastLine.points[1] - (center - boardHeight / 2)) / cellSize,
      ];
      const firstPoint = {
        x: Math.floor(preciseFirstPoint[0]),
        y: Math.floor(preciseFirstPoint[1]),
      };
      const preciseLastPoint = [
        (lastLine.points[lastLine.points.length - 2] -
          (center - boardWidth / 2)) /
          cellSize,
        (lastLine.points[lastLine.points.length - 1] -
          (center - boardHeight / 2)) /
          cellSize,
      ];
      const lastPoint = {
        x: Math.floor(preciseLastPoint[0]),
        y: Math.floor(preciseLastPoint[1]),
      };

      if (removeLine(firstPoint, lastPoint)) {
        setLines([]);
        isDrawing.current = false;
      }
    }

    const forbidden = (x: number, y: number) => {
      return (
        x < 0 ||
        x >= board.cols ||
        y < 0 ||
        y >= board.rows ||
        answers.hasBlackCell({ x, y }) ||
        board.cell[y * board.cols + x].qnum !== -1
      );
    };

    const forbiddenWithLine = (x: number, y: number) =>
      forbidden(x, y) || hasLine({ x, y });

    const cells = displayPoints.map((e, i, arr) => {
      const precisePoint = [
        (e[0] - (center - boardWidth / 2)) / cellSize,
        (e[1] - (center - boardHeight / 2)) / cellSize,
      ];
      const point = [Math.floor(precisePoint[0]), Math.floor(precisePoint[1])];
      const neighborXPoint = [
        -1 + 2 * Math.round(precisePoint[0]) - Math.floor(precisePoint[0]),
        Math.floor(precisePoint[1]),
      ];
      const neighborYPoint = [
        Math.floor(precisePoint[0]),
        -1 + 2 * Math.round(precisePoint[1]) - Math.floor(precisePoint[1]),
      ];
      const neighborPoint = forbiddenWithLine(
        neighborXPoint[0],
        neighborXPoint[1]
      )
        ? neighborYPoint
        : forbiddenWithLine(neighborYPoint[0], neighborYPoint[1])
        ? neighborXPoint
        : Math.abs((precisePoint[0] % 1) - 0.5) >
          Math.abs((precisePoint[1] % 1) - 0.5)
        ? neighborXPoint
        : neighborYPoint;
      return {
        point,
        neighborPoint,
      };
    });

    const lastPoint = cells[cells.length - 1].point;
    const lastNeighborPoint = cells[cells.length - 1].neighborPoint;
    if (
      forbiddenWithLine(lastPoint[0], lastPoint[1]) &&
      forbiddenWithLine(lastNeighborPoint[0], lastNeighborPoint[1])
    ) {
      setLines([]);
      isDrawing.current = false;
      return;
    }

    let normalizedPoints = [cells[0].point];
    for (let i = 1; i < cells.length; i++) {
      const last = normalizedPoints[normalizedPoints.length - 1] as [
        number,
        number
      ];
      const current = cells[i];
      if (last[0] === current.point[0] && last[1] === current.point[1]) {
        continue;
      }
      if (forbiddenWithLine(current.point[0], current.point[1])) {
        if (
          forbiddenWithLine(current.neighborPoint[0], current.neighborPoint[1])
        ) {
          continue;
        }
        const points = pathfinder(
          last,
          current.neighborPoint as [number, number],
          forbiddenWithLine
        );
        if (points === null) {
          setLines([]);
          isDrawing.current = false;
          return;
        }
        normalizedPoints.push(...points);
      } else {
        const points = pathfinder(
          last,
          current.point as [number, number],
          forbiddenWithLine
        );
        if (points === null) {
          setLines([]);
          isDrawing.current = false;
          return;
        }
        normalizedPoints.push(...points);
      }
    }
    if (forbiddenWithLine(lastNeighborPoint[0], lastNeighborPoint[1])) {
      normalizedPoints.push([lastPoint[0], lastPoint[1]]);
    }

    let flag = true;
    do {
      flag = true;
      const memo = new Map<string, number>();
      for (let i = 0; i < normalizedPoints.length; i++) {
        const point = normalizedPoints[i];
        const key = point[0] + "," + point[1];
        if (memo.has(key)) {
          normalizedPoints.splice(
            memo.get(key) as number,
            i - (memo.get(key) as number)
          );
          flag = false;
          break;
        } else {
          memo.set(key, i);
        }
      }
    } while (!flag);

    if (normalizedPoints.length <= 1) {
      setLines([]);
      isDrawing.current = false;
      return;
    }
    setLines([]);
    addLine(normalizedPoints.map((p) => ({ x: p[0], y: p[1] })));
    isDrawing.current = false;
  };

  if (puzzleType === "yajilinAuto" && redraw < 1000) {
    return <></>;
  }
  return (
    <>
      <Stage
        scale={{ x: fullHeight / 2000, y: fullHeight / 2000 }}
        width={fullWidth}
        height={fullHeight}
        x={(fullWidth - fullHeight) / 2}
      >
        <Layer>
          {[...Array(board.cols)].map((_, x) =>
            [...Array(board.rows)]
              .map(
                (_, y) =>
                  board.cell[y * board.cols + x].qnum !== -1 &&
                  [
                    ,
                    <NumberWithTopArrow
                      x={x}
                      y={y}
                      num={board.cell[y * board.cols + x].qnum}
                      key={x + "," + y}
                      center={center}
                      boardWidth={boardWidth}
                      boardHeight={boardHeight}
                      cellSize={cellSize}
                    />,
                    <NumberWithBottomArrow
                      x={x}
                      y={y}
                      num={board.cell[y * board.cols + x].qnum}
                      key={x + "," + y}
                      center={center}
                      boardWidth={boardWidth}
                      boardHeight={boardHeight}
                      cellSize={cellSize}
                    />,
                    <NumberWithLeftArrow
                      x={x}
                      y={y}
                      num={board.cell[y * board.cols + x].qnum}
                      key={x + "," + y}
                      center={center}
                      boardWidth={boardWidth}
                      boardHeight={boardHeight}
                      cellSize={cellSize}
                    />,
                    <NumberWithRightArrow
                      x={x}
                      y={y}
                      num={board.cell[y * board.cols + x].qnum}
                      key={x + "," + y}
                      center={center}
                      boardWidth={boardWidth}
                      boardHeight={boardHeight}
                      cellSize={cellSize}
                    />,
                  ][board.cell[y * board.cols + x].qdir]
              )
              .filter((e) => e)
          )}
          {
            <Html>
              <ReactRough width={center * 2} height={center * 2} config={{}}>
                {[...Array(board.cols)].map((_, x) =>
                  [...Array(board.rows)].map((_, y) => (
                    <Rectangle
                      key={"rough" + x + "," + y}
                      fill={
                        answers.hasBlackCell({ x, y })
                          ? "rgba(64,64,64,0.8)"
                          : "rgba(64,64,64,0)"
                      }
                      height={cellSize * 0.8}
                      width={cellSize * 0.8}
                      x={
                        center - boardWidth / 2 + cellSize * 0.07 + x * cellSize
                      }
                      y={
                        center -
                        boardHeight / 2 +
                        cellSize * 0.07 +
                        y * cellSize +
                        ((answers.hasBlackCell({ x, y }) ? 1 : 0) *
                          Math.random()) /
                          10000
                      }
                      fillStyle={"zigzag"}
                      hachureGap={12}
                      fillWeight={3}
                      roughness={1}
                      stroke={"rgba(64,64,64,0)"}
                      seed={(x * board.cols + y) * 10000 + 4123}
                    />
                  ))
                )}
              </ReactRough>

              <Stage width={center * 2} height={center * 2} x={0}>
                <Layer>
                  <Line
                    x={center - boardWidth / 2 + cx * cellSize}
                    y={center - boardHeight / 2 + cy * cellSize}
                    points={[
                      0.01 * cellSize,
                      0.01 * cellSize,
                      0.01 * cellSize,
                      0.01 * cellSize,
                      0.99 * cellSize,
                      0.01 * cellSize,
                      0.01 * cellSize,
                      0.99 * cellSize,
                    ]}
                    width={cellSize}
                    height={cellSize}
                    fill={"white"}
                    stroke={"white"}
                    closed
                    ref={overRef1}
                  />
                  <Line
                    x={center - boardWidth / 2 + cx * cellSize}
                    y={center - boardHeight / 2 + cy * cellSize}
                    points={[
                      0.99 * cellSize,
                      0.01 * cellSize,
                      0.01 * cellSize,
                      0.99 * cellSize,
                      0.99 * cellSize,
                      0.99 * cellSize,
                    ]}
                    width={cellSize}
                    height={cellSize}
                    fill={"white"}
                    stroke={"white"}
                    closed
                    ref={overRef2}
                  />
                  <Rect
                    x={center - boardWidth / 2}
                    y={center - boardHeight / 2}
                    width={boardWidth}
                    height={boardHeight}
                    stroke={"black"}
                    strokeWidth={cellSize * 0.1}
                  />
                  {[...Array(board.cols)].map((_, i) => (
                    <Line
                      x={center}
                      y={center}
                      key={i}
                      stroke={"black"}
                      strokeWidth={cellSize * 0.02}
                      points={[
                        -boardWidth / 2 + cellSize * i,
                        -boardHeight / 2,
                        -boardWidth / 2 + cellSize * i,
                        +boardHeight / 2,
                      ]}
                    />
                  ))}
                  {[...Array(board.rows)].map((_, i) => (
                    <Line
                      x={center}
                      y={center}
                      key={i}
                      stroke={"black"}
                      strokeWidth={cellSize * 0.02}
                      points={[
                        -boardWidth / 2,
                        -boardHeight / 2 + cellSize * i,
                        +boardWidth / 2,
                        -boardHeight / 2 + cellSize * i,
                      ]}
                    />
                  ))}
                  {[...answers.horizontal]
                    .filter((e) => e[1] === LineState.Bar)
                    .map((e, i) => {
                      const { x, y } = toPos(e[0]);
                      return (
                        <>
                          <Line
                            x={center}
                            y={center}
                            key={"horizontal--" + i}
                            stroke={"green"}
                            strokeWidth={cellSize * 0.02}
                            points={[
                              -boardWidth / 2 + cellSize * (x + 1) - 10,
                              -boardHeight / 2 + cellSize * (y + 0.5) - 10,
                              -boardWidth / 2 + cellSize * (x + 1) + 10,
                              -boardHeight / 2 + cellSize * (y + 0.5) + 10,
                            ]}
                          />
                          <Line
                            x={center}
                            y={center}
                            key={"vertical--" + i}
                            stroke={"green"}
                            strokeWidth={cellSize * 0.02}
                            points={[
                              -boardWidth / 2 + cellSize * (x + 1) - 10,
                              -boardHeight / 2 + cellSize * (y + 0.5) + 10,
                              -boardWidth / 2 + cellSize * (x + 1) + 10,
                              -boardHeight / 2 + cellSize * (y + 0.5) - 10,
                            ]}
                          />
                        </>
                      );
                    })}
                  {[...answers.vertical]
                    .filter((e) => e[1] === LineState.Bar)
                    .map((e, i) => {
                      const { x, y } = toPos(e[0]);
                      return (
                        <>
                          <Line
                            x={center}
                            y={center}
                            key={"horizontal-/-" + i}
                            stroke={"green"}
                            strokeWidth={cellSize * 0.02}
                            points={[
                              -boardWidth / 2 + cellSize * (x + 0.5) - 10,
                              -boardHeight / 2 + cellSize * (y + 1) - 10,
                              -boardWidth / 2 + cellSize * (x + 0.5) + 10,
                              -boardHeight / 2 + cellSize * (y + 1) + 10,
                            ]}
                          />
                          <Line
                            x={center}
                            y={center}
                            key={"vertical-/-" + i}
                            stroke={"green"}
                            strokeWidth={cellSize * 0.02}
                            points={[
                              -boardWidth / 2 + cellSize * (x + 0.5) - 10,
                              -boardHeight / 2 + cellSize * (y + 1) + 10,
                              -boardWidth / 2 + cellSize * (x + 0.5) + 10,
                              -boardHeight / 2 + cellSize * (y + 1) - 10,
                            ]}
                          />
                        </>
                      );
                    })}
                  {lines.map((line, i) => (
                    <Line
                      key={i}
                      points={line.points}
                      stroke={line.tool === "eraser" ? "darkred" : "green"}
                      strokeWidth={5}
                      opacity={0.5}
                      tension={0.5}
                      lineCap="round"
                      lineJoin="round"
                      globalCompositeOperation={"source-over"}
                    />
                  ))}
                </Layer>
              </Stage>
            </Html>
          }
        </Layer>
        <Layer>
          <Html>
            <Stage
              width={center * 2}
              height={center * 2}
              x={0}
              onMouseDown={handleMouseDown}
              onMousemove={handleMouseMove}
              onMouseup={handleMouseUp}
            >
              <Layer>
                {[...Array(board.cols)].map((_, x) =>
                  [...Array(board.rows)].map((_, y) => (
                    <Rect
                      key={x + "," + y}
                      x={
                        center - boardWidth / 2 + x * cellSize + 0.45 * cellSize
                      }
                      opacity={answers.hasWhiteCell({ x, y }) ? 1 : 0}
                      y={
                        center -
                        boardHeight / 2 +
                        y * cellSize +
                        0.45 * cellSize
                      }
                      width={0.1 * cellSize}
                      height={0.1 * cellSize}
                      fill={"green"}
                    />
                  ))
                )}
                {[...Array(board.cols)].map((_, x) =>
                  [...Array(board.rows)].map((_, y) => (
                    <Rect
                      key={x + "," + y}
                      x={center - boardWidth / 2 + x * cellSize}
                      y={center - boardHeight / 2 + y * cellSize}
                      width={cellSize}
                      height={cellSize}
                      opacity={0}
                      onContextMenu={(e) => {
                        e.evt.preventDefault();
                        onRightClick(x, y);
                      }}
                      onClick={(e) => {
                        e.evt.preventDefault();
                        e.evt.button === 0 && onClick(x, y);
                      }}
                    />
                  ))
                )}
                {fixedLines.map((line, i) => (
                  <Line
                    key={i}
                    points={line
                      .map((p) => [
                        (p.x + 0.5) * cellSize + center - boardWidth / 2,
                        (p.y + 0.5) * cellSize + center - boardHeight / 2,
                      ])
                      .flat()}
                    stroke="green"
                    strokeWidth={10}
                    opacity={1}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    onMouseEnter={(e) => {
                      e.target.to({
                        strokeWidth: 20,
                        duration: 0.15,
                      });
                    }}
                    onMouseOut={(e) => {
                      e.target.to({
                        strokeWidth: 10,
                        duration: 0.15,
                      });
                    }}
                  />
                ))}
              </Layer>
            </Stage>
          </Html>
        </Layer>
      </Stage>
    </>
  );
};

const ReactRough = styled(_ReactRough)`
  pointer-events: none;
`;
const Html = styled(_Html)`
  pointer-events: none;
`;
